import { graphGetJson, graphPostJson } from "../../email/services/graph";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import type { WorkingHours } from "../types";

interface GraphWorkingHoursDto {
  readonly daysOfWeek?: readonly string[];
  readonly startTime?: string;
  readonly endTime?: string;
}

interface ScheduleResponse {
  readonly value?: readonly {
    readonly workingHours?: GraphWorkingHoursDto | null;
    readonly error?: { readonly responseCode?: string } | null;
  }[];
}

const DAY_INDEX: Readonly<Record<string, number>> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/** "08:00:00.0000000" → 480. */
function parseTimeOfDay(value: string | undefined): number | null {
  const m = /^(\d{2}):(\d{2})/.exec(value ?? "");
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Graph workingHours → WorkingHours, or null when incomplete. */
export function parseWorkingHours(dto: GraphWorkingHoursDto | null | undefined): WorkingHours | null {
  if (!dto) return null;
  const days = new Set(
    (dto.daysOfWeek ?? [])
      .map((d) => DAY_INDEX[d.toLowerCase()])
      .filter((d): d is number => d !== undefined),
  );
  const startMin = parseTimeOfDay(dto.startTime);
  const endMin = parseTimeOfDay(dto.endTime);
  if (days.size === 0 || startMin === null || endMin === null || endMin <= startMin) return null;
  return { days, startMin, endMin, source: "outlook" };
}

/*
 * Working hours are applied as local wall-clock times; converting between the
 * Outlook work-hours time zone and the computer's zone isn't done yet.
 */

/**
 * The user's working hours straight from their mailbox settings (Outlook →
 * Settings → Calendar → Work hours). Needs MailboxSettings.Read.
 */
export async function fetchMailboxWorkingHours(
  token: string,
  refresh: RefreshTokenFn | undefined,
): Promise<WorkingHours | null> {
  const dto = await graphGetJson<GraphWorkingHoursDto>("/me/mailboxSettings/workingHours", token, refresh);
  return parseWorkingHours(dto);
}

/**
 * The user's working hours as returned alongside free/busy. Used when mailbox
 * settings can't be read; needs only calendar read. Exchange can fail a single
 * schedule inside a successful response (e.g. "Proxy web request failed",
 * 5016), which is reported back as `errorCode`.
 */
export async function fetchScheduleWorkingHours(
  token: string,
  refresh: RefreshTokenFn | undefined,
  email: string,
): Promise<{ hours: WorkingHours | null; errorCode: string | null }> {
  const start = new Date();
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  const result = await graphPostJson<ScheduleResponse>(
    "/me/calendar/getSchedule",
    {
      schedules: [email],
      startTime: { dateTime: start.toISOString().slice(0, 19), timeZone: "UTC" },
      endTime: { dateTime: end.toISOString().slice(0, 19), timeZone: "UTC" },
      availabilityViewInterval: 60,
    },
    token,
    refresh,
  );
  const entry = result.value?.[0];
  return {
    hours: parseWorkingHours(entry?.workingHours),
    errorCode: entry?.error ? (entry.error.responseCode ?? "unknown") : null,
  };
}
